"use client";
import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";

import { ArrowRight, Calendar, Plus } from "lucide-react";

import { CertificationAutocomplete } from "@/components/certification-autocomplete";
import { certNamesFromProfile } from "@/lib/focus-session-shared";

const CERT_FIELD_HELP =
  "This plan is for the certification shown. To study a different certification, create a new study plan for that cert.";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getEmailValidationError } from "@/lib/emailValidation";
import { safeFormatDate } from "@/lib/safe-date";
import {
  addHoursToTime,
  downloadStudyPlanIcs,
  groupPlanByMonth,
} from "@/lib/study-plan-schedule";
import { trackGaEvent } from "@/lib/ga";
import { PlanIntro, WeekCalendar } from "@/components/destination-layouts/kit";
import { AmPmTimeInput, StudyDayTimeRow } from "@/components/study-plans/am-pm-time-input";

interface Section {
  name: string;
  hours: number;
}

interface StudyPlanDay {
  date: string;
  sections: { name: string; hours: number }[];
  complete: boolean;
  hours: number;
  startTime?: string;
  endTime?: string;
}

const PLANNER_EXAM_LINKS = [
  {
    name: "NCLEX-RN",
    detail: "Study timelines, exam details, and preparation resources",
    href: "/certifications/nclex-rn",
  },
  {
    name: "ATI TEAS",
    detail: "Exam format, sections, and preparation guidance",
    href: "/certifications/ati-teas",
  },
  {
    name: "CCMA",
    detail: "Certification requirements, exam details, and study resources",
    href: "/certifications/ccma",
  },
  {
    name: "CAPM",
    detail: "Exam cost, requirements, and preparation guidance",
    href: "/certifications/capm",
  },
  {
    name: "Professional Scrum Master I",
    detail: "Exam details and preparation resources",
    href: "/certifications/professional-scrum-master-psm-i",
  },
] as const;

const DAYS_OF_WEEK = [
  { label: "Sunday", short: "Sun", value: 0 },
  { label: "Monday", short: "Mon", value: 1 },
  { label: "Tuesday", short: "Tue", value: 2 },
  { label: "Wednesday", short: "Wed", value: 3 },
  { label: "Thursday", short: "Thu", value: 4 },
  { label: "Friday", short: "Fri", value: 5 },
  { label: "Saturday", short: "Sat", value: 6 },
];

function DetailsStartOpen({
  startOpen = false,
  className,
  children,
}: {
  startOpen?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  // React 18 types have `open` on <details>, not `defaultOpen`.
  // Same spread used by app/certifications/[slug]/page.tsx CollapsibleCard.
  return (
    <details className={className} {...(startOpen ? { open: true } : {})}>
      {children}
    </details>
  );
}

const DEFAULT_STUDY_START = "18:00";
const DEFAULT_STUDY_HOURS = "2";
const DEFAULT_WEEKDAY_HOURS: { [key: number]: string } = {
  1: DEFAULT_STUDY_HOURS,
  2: DEFAULT_STUDY_HOURS,
  3: DEFAULT_STUDY_HOURS,
  4: DEFAULT_STUDY_HOURS,
  5: DEFAULT_STUDY_HOURS,
};

function defaultStartTimes(): { [key: number]: string } {
  return { 0: DEFAULT_STUDY_START, 1: DEFAULT_STUDY_START, 2: DEFAULT_STUDY_START, 3: DEFAULT_STUDY_START, 4: DEFAULT_STUDY_START, 5: DEFAULT_STUDY_START, 6: DEFAULT_STUDY_START };
}

function defaultEndTimes(hoursByDay: { [key: number]: string }): { [key: number]: string } {
  const ends: { [key: number]: string } = {};
  for (let day = 0; day <= 6; day++) {
    ends[day] = addHoursToTime(DEFAULT_STUDY_START, Number(hoursByDay[day] || DEFAULT_STUDY_HOURS));
  }
  return ends;
}

export default function StudySchedulePage() {
  const [certification, setCertification] = useState("");
  const [examDate, setExamDate] = useState("");
  const [email, setEmail] = useState("");
  const [sections, setSections] = useState<Section[]>([]);
  const [sectionName, setSectionName] = useState("");
  const [sectionHours, setSectionHours] = useState("");
  const [studyPlan, setStudyPlan] = useState<StudyPlanDay[]>([]);
  const [completedDays, setCompletedDays] = useState<Set<number>>(new Set());
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]); // Default: Mon-Fri
  const [studyDuration, setStudyDuration] = useState(DEFAULT_STUDY_HOURS);
  const [preferredStart, setPreferredStart] = useState(DEFAULT_STUDY_START);
  const [customizeEachDay, setCustomizeEachDay] = useState(false);
  const [dailyHours, setDailyHours] = useState<{ [key: number]: string }>(DEFAULT_WEEKDAY_HOURS);
  const [dailyStartTimes, setDailyStartTimes] = useState<{ [key: number]: string }>(defaultStartTimes);
  const [dailyEndTimes, setDailyEndTimes] = useState<{ [key: number]: string }>(
    defaultEndTimes(DEFAULT_WEEKDAY_HOURS)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [planWarning, setPlanWarning] = useState("");
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [emailSendError, setEmailSendError] = useState("");
  const [emailError, setEmailError] = useState("");
  const planRef = useRef<HTMLDivElement>(null);
  const plannerStartedRef = useRef(false);

  const emailValue = email ?? "";

  function markPlannerStarted() {
    if (plannerStartedRef.current) return;
    plannerStartedRef.current = true;
    trackGaEvent("study_planner_started");
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("certification") ?? params.get("cert");
    if (fromQuery?.trim()) {
      setCertification(fromQuery.trim());
      return;
    }
    let cancelled = false;
    fetch("/api/user/social-profile", { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        const name = certNamesFromProfile(data?.socialProfile)[0] || "";
        if (name) setCertification((current) => current || name);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setEmailError(getEmailValidationError(emailValue));
  }, [emailValue]);

  useEffect(() => {
    if (certification || examDate || emailValue || sections.length > 0) {
      markPlannerStarted();
    }
  }, [certification, examDate, emailValue, sections.length]);

  function addSection() {
    if (
      !sectionName.trim() ||
      !sectionHours ||
      isNaN(Number(sectionHours)) ||
      Number(sectionHours) <= 0
    ) {
      alert("Please enter a valid section name and hours");
      return;
    }
    setSections((prev) => {
      const updated = [...prev, { name: sectionName.trim(), hours: Number(sectionHours) }];
      if (updated.length > 0 && formErrors.sections) {
        setFormErrors((prevErrs) => ({ ...prevErrs, sections: "" }));
      }
      return updated;
    });
    setSectionName("");
    setSectionHours("");
  }

  function removeSection(idx: number) {
    setSections((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      if (updated.length > 0 && formErrors.sections) {
        setFormErrors((prevErrs) => ({ ...prevErrs, sections: "" }));
      }
      return updated;
    });
  }

  function applySharedSchedule(days: number[], hoursValue: string, startValue: string) {
    const hours = Number(hoursValue);
    if (!(hours > 0)) return;
    setDailyHours((prev) => {
      const next = { ...prev };
      for (const day of days) next[day] = String(hours);
      return next;
    });
    setDailyStartTimes((prev) => {
      const next = { ...prev };
      for (const day of days) next[day] = startValue;
      return next;
    });
    setDailyEndTimes((prev) => {
      const next = { ...prev };
      for (const day of days) next[day] = addHoursToTime(startValue, hours);
      return next;
    });
  }

  function handleDayToggle(day: number) {
    const turningOn = !selectedDays.includes(day);
    const nextDays = turningOn
      ? [...selectedDays, day].sort()
      : selectedDays.filter((d) => d !== day);
    setSelectedDays(nextDays);
    setDailyHours((prev) => {
      const newHours = { ...prev };
      if (turningOn) newHours[day] = studyDuration || DEFAULT_STUDY_HOURS;
      else delete newHours[day];
      return newHours;
    });
    if (turningOn) {
      const hours = Number(studyDuration) > 0 ? Number(studyDuration) : Number(DEFAULT_STUDY_HOURS);
      setDailyStartTimes((prev) => ({ ...prev, [day]: preferredStart || DEFAULT_STUDY_START }));
      setDailyEndTimes((prev) => ({
        ...prev,
        [day]: addHoursToTime(preferredStart || DEFAULT_STUDY_START, hours),
      }));
    }
  }

  function handleSharedDurationChange(value: string) {
    setStudyDuration(value);
    applySharedSchedule(selectedDays, value, preferredStart);
  }

  function handlePreferredStartChange(value: string) {
    setPreferredStart(value);
    applySharedSchedule(selectedDays, studyDuration, value);
  }

  function handleDailyHourChange(day: number, value: string) {
    setDailyHours((prev) => ({ ...prev, [day]: value }));
    const hours = Number(value);
    if (hours > 0) {
      const start = dailyStartTimes[day] || DEFAULT_STUDY_START;
      setDailyEndTimes((prev) => ({ ...prev, [day]: addHoursToTime(start, hours) }));
    }
  }

  function handleStartTimeChange(day: number, value: string) {
    setDailyStartTimes((prev) => ({ ...prev, [day]: value }));
    const hours = Number(dailyHours[day] || 1);
    if (hours > 0) {
      setDailyEndTimes((prev) => ({ ...prev, [day]: addHoursToTime(value, hours) }));
    }
  }

  function handleEndTimeChange(day: number, value: string) {
    setDailyEndTimes((prev) => ({ ...prev, [day]: value }));
  }

  // Enhanced plan generation: distribute section hours across available days, respecting per-day limits
  async function generateStudyPlan() {
    setPlanWarning("");
    setSuccessMessage("");
    setEmailSendError("");
    const errors: { [key: string]: string } = {};
    
    // Enhanced email validation
    if (!email) {
      errors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Please enter a valid email address.";
    }
    
    // Exam date validation
    if (!examDate) {
      errors.examDate = "Please pick an exam date.";
    }
    // Sections validation
    if (sections.length === 0) {
      errors.sections = "Please add at least one section.";
    }
    // Study days validation
    if (selectedDays.length === 0) {
      errors.studyDays = "Please select at least one study day.";
    }
    const missingTimes = selectedDays.filter(
      (day) => !dailyStartTimes[day] || !dailyEndTimes[day]
    );
    if (missingTimes.length > 0) {
      errors.studyTimes = "Please set a start and end time for each study day.";
    } else if (selectedDays.some((day) => dailyStartTimes[day] === dailyEndTimes[day])) {
      errors.studyTimes = "End time must be different from start time.";
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    const startDate = new Date();
    const endDate = new Date(examDate);
    const validDays: { date: Date; dayOfWeek: number; maxHours: number }[] = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      if (selectedDays.includes(dayOfWeek)) {
        validDays.push({
          date: new Date(currentDate),
          dayOfWeek,
          maxHours: Number(dailyHours[dayOfWeek] || 1),
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    // 1. Calculate total required hours
    const totalRequiredHours = sections.reduce((sum, s) => sum + s.hours, 0);
    // 2. Calculate total available study hours
    const totalAvailableHours = validDays.reduce((sum, d) => sum + d.maxHours, 0);
    // 3. If not enough, show warning and suggest minimum daily hours
    if (validDays.length === 0) {
      setPlanWarning(
        "There's no study time between today and your exam date on the days you selected. Choose a later exam date or add more study days."
      );
      setStudyPlan([]);
      return;
    }
    if (totalAvailableHours < totalRequiredHours) {
      const minDailyHours = Math.ceil(totalRequiredHours / validDays.length);
      setPlanWarning(
        `You need to increase your daily study hours to at least ${minDailyHours} hour(s) per study day to complete your plan before the exam. (You have ${totalAvailableHours} available, but need ${totalRequiredHours})`
      );
      setStudyPlan([]);
      return;
    }
    // 4. Distribute section hours across days
    const studyBlocks: { name: string; hours: number }[] = [];
    sections.forEach((section) => {
      for (let i = 0; i < section.hours; i++) {
        studyBlocks.push({ name: section.name, hours: 1 });
      }
    });
    const plan: StudyPlanDay[] = [];
    let blockIdx = 0;
    for (let i = 0; i < validDays.length; i++) {
      let hoursLeft = validDays[i].maxHours;
      const daySections: { name: string; hours: number }[] = [];
      while (hoursLeft > 0 && blockIdx < studyBlocks.length) {
        const block = studyBlocks[blockIdx];
        if (
          daySections.length > 0 &&
          daySections[daySections.length - 1].name === block.name
        ) {
          daySections[daySections.length - 1].hours += 1;
        } else {
          daySections.push({ name: block.name, hours: 1 });
        }
        hoursLeft--;
        blockIdx++;
      }
      plan.push({
        date: [
          validDays[i].date.getFullYear(),
          String(validDays[i].date.getMonth() + 1).padStart(2, "0"),
          String(validDays[i].date.getDate()).padStart(2, "0"),
        ].join("-"),
        sections: daySections,
        complete: false,
        hours: validDays[i].maxHours - hoursLeft,
        startTime: dailyStartTimes[validDays[i].dayOfWeek] || DEFAULT_STUDY_START,
        endTime:
          dailyEndTimes[validDays[i].dayOfWeek] ||
          addHoursToTime(dailyStartTimes[validDays[i].dayOfWeek] || DEFAULT_STUDY_START, validDays[i].maxHours),
      });
    }
    setStudyPlan(plan);
    setCompletedDays(new Set());
    trackGaEvent("study_plan_generated", {
      certification: certification || "unknown",
      days: plan.length,
    });
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/study-plan/send-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          certification,
          examDate,
          sections,
          plan,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setEmailSendError(
          data.detail
            ? `${data.error || "Failed to send study plan email"}: ${data.detail}`
            : data.error ||
              `We couldn't email your plan (${res.status}). You can still print it or download the calendar invite from the plan.`
        );
        setSuccessMessage("Your study plan has been generated!");
      } else {
        setSuccessMessage(
          "Your study plan has been generated. We emailed you a PDF. Use Add to Calendar on this page to download the invite."
        );
      }
    } catch {
      setEmailSendError(
        "We couldn't email your plan. You can still print it or add it to your calendar here."
      );
      setSuccessMessage("Your study plan has been generated!");
    } finally {
      setIsSubmitting(false);
    }
  }

  function toggleDayComplete(idx: number) {
    setCompletedDays((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  function handlePrint() {
    const root = planRef.current;
    if (!root) {
      window.print();
      return;
    }
    const details = Array.from(root.querySelectorAll("details"));
    const wasOpen = details.map((el) => el.open);
    details.forEach((el) => {
      el.open = true;
    });
    root.classList.add("print-plan");
    document.body.classList.add("printing-study-plan");
    let restored = false;
    const restore = () => {
      if (restored) return;
      restored = true;
      details.forEach((el, i) => {
        el.open = wasOpen[i];
      });
      root.classList.remove("print-plan");
      document.body.classList.remove("printing-study-plan");
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
    window.setTimeout(restore, 2000);
  }

  function handleAddToCalendar() {
    downloadStudyPlanIcs({
      certification,
      examDate,
      plan: studyPlan,
    });
  }

  const monthGroups = groupPlanByMonth(studyPlan);

  return (
    <>
      <div className="min-h-screen bg-white print:bg-white">
        <section className="max-w-[1200px] mx-auto px-4 sm:px-6 pt-14 pb-6 text-center print:hidden">
          <PlanIntro />
        </section>

        <section className="bg-[#eef4fb] py-10 print:bg-white print:py-0">
          <div className="max-w-[1200px] mx-auto px-4 print:max-w-none print:px-0">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.2fr)] gap-8 w-full items-start print:block">
              {/* Left: Form */}
              <Card className="min-w-0 print:hidden h-full rounded-2xl border border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Study Planner</CardTitle>
            <Button variant="outline" size="sm" onClick={() => {
              setCertification("");
              setExamDate("");
              setEmail("");
              setSections([]);
              setSectionName("");
              setSectionHours("");
              setStudyPlan([]);
              setCompletedDays(new Set());
              setSelectedDays([1, 2, 3, 4, 5]);
              setStudyDuration(DEFAULT_STUDY_HOURS);
              setPreferredStart(DEFAULT_STUDY_START);
              setCustomizeEachDay(false);
              setDailyHours(DEFAULT_WEEKDAY_HOURS);
              setDailyStartTimes(defaultStartTimes());
              setDailyEndTimes(defaultEndTimes(DEFAULT_WEEKDAY_HOURS));
              setPlanWarning("");
              setFormErrors({});
              setSuccessMessage("");
              setEmailSendError("");
            }}>
              Reset All
            </Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Your exam
                </p>
              <div className="space-y-3">
            <Label htmlFor="certification">What you&apos;re preparing for</Label>
            <CertificationAutocomplete
              value={certification}
              onChange={setCertification}
              placeholder="Exam, license, or certification"
              allowExploring
            />
            <p className="text-sm text-gray-500">{CERT_FIELD_HELP}</p>
          </div>
              <div className="space-y-3">
            <Label htmlFor="examDate">Exam Date</Label>
            <Input
              id="examDate"
              type="date"
              value={examDate}
                  onChange={(e) => {
                    setExamDate(e.target.value);
                    if (formErrors.examDate) setFormErrors((prev) => ({ ...prev, examDate: "" }));
                  }}
                  aria-invalid={!!formErrors.examDate}
                  aria-describedby="examDate-error"
                />
                {formErrors.examDate && (
                  <p id="examDate-error" className="text-red-600 text-sm mt-1">{formErrors.examDate}</p>
                )}
          </div>
              <div className="space-y-4 border border-muted rounded-md p-4">
            <Label>Topics to cover</Label>
                <p className="text-sm text-gray-500">
                  Add every topic from your exam so the plan covers the full outline. A common way to set hours is in proportion to each topic&apos;s official exam weight.
                </p>
                <div className="flex gap-2 mt-3 mb-4 items-center">
              <Input
                type="text"
                value={sectionName}
                onChange={(e) => setSectionName(e.target.value)}
                placeholder="Section name"
                    className="flex-1 min-w-0"
              />
              <Input
                type="number"
                min="1"
                value={sectionHours}
                onChange={(e) => setSectionHours(e.target.value)}
                placeholder="Hours"
                    className="w-24 shrink-0"
              />
              <Button
                type="button"
                size="icon"
                onClick={addSection}
                className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
                aria-label="Add topic"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
                <ul className="m-0 p-0 list-none">
              {sections.map((section, idx) => (
                <li
                  key={idx}
                      className="flex items-center gap-2 mb-3"
                >
                      <span className="flex-1">{section.name}</span>
                      <span className="w-16">{section.hours} hrs</span>
                  <Button
                    type="button"
                    onClick={() => removeSection(idx)}
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
                {formErrors.sections && (
                  <p className="text-red-600 text-sm mt-1">{formErrors.sections}</p>
                )}
          </div>
              </div>
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Your availability
                </p>
              <div className="space-y-3">
            <Label>Study Days of the Week</Label>
                <div className="grid grid-cols-7 gap-2 mt-3">
              {DAYS_OF_WEEK.map((day) => (
                <Button
                  key={day.value}
                  type="button"
                  size="sm"
                      variant={selectedDays.includes(day.value) ? "default" : "outline"}
                  onClick={() => handleDayToggle(day.value)}
                      aria-label={day.label}
                      aria-pressed={selectedDays.includes(day.value)}
                      className={`min-w-0 px-0 overflow-hidden ${selectedDays.includes(day.value) ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}`}
                >
                  {day.short}
                </Button>
              ))}
            </div>
                {formErrors.studyDays && (
                  <p className="text-red-600 text-sm mt-1">{formErrors.studyDays}</p>
                )}
          </div>
              <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
              <div className="space-y-3">
                <Label htmlFor="studyDuration">Study duration</Label>
                <div className="flex h-9 items-center gap-2">
                  <Input
                    id="studyDuration"
                    type="number"
                    min="1"
                    max="24"
                    value={studyDuration}
                    onChange={(e) => handleSharedDurationChange(e.target.value)}
                    className="!h-9 !w-16 !rounded-md px-2 text-center text-sm shadow-none"
                    aria-label="Study duration in hours"
                  />
                  <span className="text-sm text-gray-600">hours</span>
                </div>
              </div>
              <div className="space-y-3">
                <Label>Preferred time</Label>
                <AmPmTimeInput
                  value={preferredStart}
                  onChange={handlePreferredStartChange}
                  compact
                  aria-label="Preferred start time"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              This duration and start time apply to every day you selected. Calendar holds use the start time so the block shows as busy.
            </p>
            <button
              type="button"
              aria-expanded={customizeEachDay}
              onClick={() => setCustomizeEachDay((open) => !open)}
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Customize each day
            </button>
            {customizeEachDay && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500 leading-relaxed">
                  Set a different length or start and end time for any selected day.
                  Changing the duration or time above resets every selected day.
                </p>
                {DAYS_OF_WEEK.map((day) => {
                  const enabled = selectedDays.includes(day.value);
                  return (
                    <StudyDayTimeRow
                      key={day.value}
                      label={day.label}
                      enabled={enabled}
                      hours={dailyHours[day.value] || ""}
                      onHoursChange={(value) => handleDailyHourChange(day.value, value)}
                      start={dailyStartTimes[day.value] || ""}
                      onStartChange={(value) => handleStartTimeChange(day.value, value)}
                      end={dailyEndTimes[day.value] || ""}
                      onEndChange={(value) => handleEndTimeChange(day.value, value)}
                    />
                  );
                })}
              </div>
            )}
                {formErrors.studyTimes && (
                  <p className="text-red-600 text-sm mt-1">{formErrors.studyTimes}</p>
                )}
          </div>
              </div>
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Your plan
                </p>
              <div className="space-y-3">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (formErrors.email) setFormErrors((prev) => ({ ...prev, email: "" }));
              }}
              placeholder="your.email@example.com"
              aria-invalid={!!formErrors.email}
              aria-describedby="email-hint email-error"
            />
                <p id="email-hint" className="text-xs text-gray-500 leading-relaxed">
                  We&apos;ll email you a PDF of this plan. Use Add to Calendar on this page to download the invite. This is a one-time message — we don&apos;t send recurring study reminders.
                </p>
                {(formErrors.email || emailError) && (
                  <p id="email-error" className="text-red-600 text-sm mt-1">{formErrors.email || emailError}</p>
                )}
          </div>
              <Button type="button" onClick={generateStudyPlan} className="w-full mt-4" disabled={isSubmitting || !!emailError}>
                {isSubmitting ? "Sending your plan…" : "Generate Study Plan"}
              </Button>
              {planWarning && (
                <div className="mt-2 rounded-md border border-red-300 bg-red-50 px-3 py-3 text-sm text-red-700">
                  {planWarning}
                </div>
              )}
              {Object.values(formErrors).filter(Boolean).length > 0 && (
                <div className="mt-2 space-y-1">
                  {Object.values(formErrors).filter(Boolean).map((err, idx) => (
                    <p key={idx} className="text-red-600 text-sm">{err}</p>
                  ))}
                </div>
              )}
              {successMessage && (
                <div className="mt-2">
                  <p className="text-green-600 text-sm">{successMessage}</p>
                </div>
              )}
              {emailSendError && (
                <div className="mt-2">
                  <p className="text-red-600 text-sm">{emailSendError}</p>
                </div>
              )}
              </div>
        </form>
          </CardContent>
        </Card>
              {/* Right: Generated Plan or week preview */}
              {studyPlan.length === 0 ? (
                <div className="min-w-0 print:hidden">
                  <WeekCalendar />
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-600 mb-3">
                      A study plan keeps you organized. Study partners keep you
                      accountable.
                    </p>
                    <Link href="/study-groups">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6">
                        Find a Study Group
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <Card className="min-w-0 h-full print:h-auto print:shadow-none print:border-0 print:ring-0 rounded-2xl border border-gray-200 shadow-sm">
                  <CardContent className="p-6">
                    <div ref={planRef} id="study-plan-print">
              <div className="print-plan-header hidden print:block mb-5">
                <img
                  src="/main-navigation-logo.png"
                  alt="CertLynx"
                  className="h-8 w-auto mb-3"
                />
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                  Study Plan
                </p>
                <h2 className="text-2xl font-bold text-gray-900 mt-1">
                  {certification || "Your Personalized Study Plan"}
                </h2>
                {studyPlan.length > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {safeFormatDate(studyPlan[0].date, { month: "short", day: "numeric" })}
                    {" – "}
                    {safeFormatDate(studyPlan[studyPlan.length - 1].date, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                    {examDate
                      ? ` · Exam ${safeFormatDate(examDate, { month: "long", day: "numeric", year: "numeric" })}`
                      : ""}
                    {` · ${studyPlan.length} study days`}
                  </p>
                )}
                <p className="text-sm text-gray-600 mt-3">
                  This study plan was generated at{" "}
                  <a href="https://www.certlynx.com" className="text-blue-600 underline">
                    certlynx.com
                  </a>
                  . Congratulations on getting started!
                </p>
                <p className="text-sm text-gray-900 mt-1">
                  Next step:{" "}
                  <a href="https://www.certlynx.com/study-groups" className="text-blue-600 underline">
                    Join a Study Group
                  </a>
                  .
                </p>
              </div>
                <div className="print:hidden mb-4 flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    onClick={handleAddToCalendar}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Download calendar invite
                  </Button>
                  <Button
                    type="button"
                    onClick={handlePrint}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Print Study Plan
                  </Button>
                </div>
                <div className="print-plan-sections mb-4 print:mb-3">
                  <h3 className="font-semibold text-base mb-1">Sections & Hours</h3>
                  <ul className="list-disc list-inside text-sm">
                    {sections.map((section, idx) => (
                      <li key={idx}>
                        {section.name} ({section.hours}h)
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-3">
                  {monthGroups.map((month, monthIdx) => (
                    <DetailsStartOpen
                      key={month.key}
                      startOpen={monthIdx === 0}
                      className="study-plan-month rounded-2xl border border-gray-200 overflow-hidden group/month"
                    >
                      <summary className="flex items-center justify-between px-4 py-3 bg-blue-50 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{month.label}</p>
                          <p className="text-xs text-gray-500">
                            {month.weeks.length} week{month.weeks.length === 1 ? "" : "s"} · {month.hours} hrs
                          </p>
                        </div>
                        <span className="text-blue-600 text-sm print:hidden group-open/month:hidden">▸</span>
                        <span className="text-blue-600 text-sm print:hidden hidden group-open/month:inline">▾</span>
                      </summary>
                      <div className="p-4 space-y-3">
                        {month.weeks.map((week, weekIdx) => (
                          <DetailsStartOpen
                            key={week.id}
                            startOpen={monthIdx === 0 && weekIdx === 0}
                            className="study-plan-week rounded-xl border border-gray-200 group/week print:break-inside-avoid"
                          >
                            <summary className="flex items-center justify-between px-4 py-2.5 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                              <p className="text-sm font-semibold text-blue-600">
                                Week {week.weekNumber} · {week.rangeLabel}
                              </p>
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-semibold text-blue-600">{week.hours} hrs</span>
                                <span className="text-gray-400 text-sm print:hidden group-open/week:hidden">▸</span>
                                <span className="text-gray-400 text-sm print:hidden hidden group-open/week:inline">▾</span>
                              </div>
                            </summary>
                            <div className="divide-y divide-gray-100 border-t border-gray-100">
                              {week.days.map((row) => (
                                <div
                                  key={row.isoDate}
                                  className={`flex items-center gap-3 px-4 py-2.5 ${
                                    row.index != null && completedDays.has(row.index) ? "bg-green-50" : ""
                                  }`}
                                >
                                  {row.index != null ? (
                                    <input
                                      type="checkbox"
                                      checked={completedDays.has(row.index)}
                                      onChange={() => toggleDayComplete(row.index as number)}
                                      className="h-4 w-4 accent-blue-600 print:hidden"
                                      aria-label={`Mark ${row.weekday} as complete`}
                                    />
                                  ) : (
                                    <span className="w-4 print:hidden" />
                                  )}
                                  <span className="w-10 text-center text-xs font-medium rounded-md bg-blue-50 text-blue-600 py-0.5">
                                    {row.weekdayShort}
                                  </span>
                                  <span
                                    className={`flex-1 text-sm ${
                                      row.isRest ? "text-gray-400" : "text-gray-700"
                                    }`}
                                  >
                                    {row.topic}
                                    {row.timeLabel ? (
                                      <span className="block text-xs text-gray-400">{row.timeLabel}</span>
                                    ) : null}
                                  </span>
                                  <span className={`text-sm ${row.isRest ? "text-gray-400" : "text-gray-500"}`}>
                                    {row.isRest ? "—" : `${row.hours} hrs`}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </DetailsStartOpen>
                        ))}
                      </div>
                    </DetailsStartOpen>
                  ))}
                </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>
        <section className="max-w-3xl mx-auto px-6 py-12 print:hidden">
          <h2 className="text-xl font-semibold text-gray-900">
            How the Free Study Planner Works
          </h2>
          <div className="mt-3 space-y-3 text-sm text-gray-600 leading-relaxed">
            <p>
              Choose your exam, test date, and the topics you need to cover. Set
              the days and hours you can study. The planner places those topics
              on the days between today and your exam date.
            </p>
            <p>
              If the hours do not fit, it tells you how much time to add instead
              of building a plan that runs past your test date. Check days off as
              you finish them, print the plan, download a calendar file, or email
              yourself a PDF.
            </p>
          </div>

          <div className="mt-10">
            <h2 className="text-xl font-semibold text-gray-900">
              Start with the exam you&apos;re preparing for
            </h2>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">
              Your study plan should fit your goal. Explore exam information and
              preparation resources, then build a schedule around your exam date
              and available study time.
            </p>
            <h3 className="mt-6 text-sm font-semibold text-gray-900">
              Popular exam resources
            </h3>
            <ul className="mt-3 grid gap-3">
              {PLANNER_EXAM_LINKS.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className="group block rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <span className="block text-sm font-semibold text-gray-900">
                      {item.name}
                    </span>
                    <span className="mt-1 block text-sm text-gray-600">
                      {item.detail}
                    </span>
                    <span className="mt-3 inline-flex items-center text-sm font-medium text-blue-600 group-hover:text-blue-700">
                      Explore {item.name}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 border-t border-gray-200 pt-10">
            <h2 className="text-xl font-semibold text-gray-900">
              Prepare with your community
            </h2>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">
              A study plan helps you stay organized. CertLynx also gives you a
              place to connect with people working toward professional credentials.
            </p>
            <p className="mt-4 text-sm">
              <Link href="/study-groups" className="font-medium text-blue-600 hover:text-blue-700 hover:underline">
                Find Study Groups
              </Link>
              <span className="mx-2 text-gray-300" aria-hidden="true">·</span>
              <Link href="/forums" className="font-medium text-blue-600 hover:text-blue-700 hover:underline">
                Join Discussions
              </Link>
              <span className="mx-2 text-gray-300" aria-hidden="true">·</span>
              <Link href="/tutors" className="font-medium text-blue-600 hover:text-blue-700 hover:underline">
                Find a Tutor
              </Link>
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
